import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

//Bootstrap
import { Form } from "react-bootstrap";

//API calls
import {
  useGetSifrantVlogaPartnerja,
  useEditClanicaUM,
} from "../../../../services/ProjectDetails/partnerskeOrganizacijeAPI";
import { useGetOsebeFromImenik } from "../../../../services/Shared/sharedAPI";

//UI components
import FormSelect from "../../../ui/FormSelect";
import FormSelectSearchAsyncField from "../../../ui/FormSelectSearchAsyncField";
import FormField from "../../../ui/FormField";
import ErrorMessage from "../../../ui/ErrorMessage";

//Utils
import { useDebounceHook } from "../../../utils/useDebounceHook";
import sifrantSetSelectValueAndLabel from "../../../utils/sifrantSetSelectValueAndLabel";

//Validation schema
import formEditClanicaUMSchema from "../../../../config/formEditClanicaUM";

const ClaniceNaProjektuCardItemEditForm = ({ clanica, setIsEditing }) => {
  const { id } = useParams();

  const [searchTermOseba, setSearchTermOseba] = useState("");

  const schema = useMemo(() => formEditClanicaUMSchema, []);

  //GET Vloge za partnerja
  const { data: sifrantVloga } = useGetSifrantVlogaPartnerja(true);

  //GET osebe from Imenik based on search term
  const { data: osebeImenik, isLoading: isLoadingOsebe } =
    useGetOsebeFromImenik(useDebounceHook(searchTermOseba, 300));

  //PUT edit clanica UM
  const {
    mutate: editClanicaUM,
    isPending: isPendingEdit,
    isError,
    error,
    reset: resetMutationError,
  } = useEditClanicaUM(id);

  //INIT form with react-hook-form and zod validation
  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    register,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      vlogaID: clanica?.vlogaID || null,
      osebaSelected:
        clanica?.kisID && clanica?.kisID !== 0
          ? { value: clanica.kisID, label: clanica.oseba }
          : null,
      skrbnik: clanica?.skrbnik || false,
    },
  });

  //setSifrantVlogaOptions in format for select component
  const sifrantVlogaOptions = sifrantSetSelectValueAndLabel(sifrantVloga);

  //setOsebeOptions in format for select component
  const osebeOptions = sifrantSetSelectValueAndLabel(
    osebeImenik,
    "kisid",
    "naziv",
  );

  //onSubmit function to call mutation for editing clanica UM
  const onSubmit = (data) => {
    const payload = {
      clanicaID: clanica.clanicaID,
      vlogaID: data.vlogaID,
      kisID: data.osebaSelected.value,
      skrbnik: data.skrbnik,
    };

    editClanicaUM(payload, {
      onSuccess: () => {
        setIsEditing(false);
      },
      onError: () => setTimeout(() => resetMutationError(), 3000),
    });
  };

  return (
    <div className="px-3 pb-3 mt-2">
      <Form onSubmit={handleSubmit((data) => onSubmit(data))}>
        <FormSelect
          label="Vloga članice"
          name="vlogaID"
          control={control}
          options={sifrantVlogaOptions}
          validationError={errors.vlogaID?.message}
        />
        <FormSelectSearchAsyncField
          label="Kontaktna oseba članice UM"
          name="osebaSelected"
          control={control}
          options={osebeOptions}
          setSearchTerm={setSearchTermOseba}
          placeholder="Iskanje kontaktne osebe..."
          isLoading={isLoadingOsebe}
          validationError={errors.osebaSelected?.message}
        />
        {!clanica?.skrbnik && (
          <FormField
            label="Izbrano članico določi za skrbnika projekta"
            formControlType="checkbox"
            name="skrbnik"
            register={register("skrbnik")}
          />
        )}
        <button
          type="submit"
          className="btn btn-primary mt-3"
          disabled={!isDirty || isPendingEdit}
        >
          {isPendingEdit ? "Shranjevanje..." : "Shrani"}
        </button>
        <button
          type="button"
          className="btn btn-secondary mt-3 ms-2"
          onClick={() => setIsEditing(false)}
        >
          Prekliči
        </button>
        {isError && <ErrorMessage message={error.message} />}
      </Form>
    </div>
  );
};

export default ClaniceNaProjektuCardItemEditForm;
